import React, { ReactNode, useMemo } from 'react';

import { BaseNotificationProps, MatchByType, NotificationProps } from './types';
import { NotificationType } from 'app/api/services/notifications/types';

import CurrencySymbol from 'app/design-system/components/CurrencySymbol';
import { Notification as BaseNotification, NotificationStatus } from 'app/design-system/components/Notification';
import { useTranslation } from 'app/lang';
import { formatMoney } from 'app/lib/formatters';
import { ROUTE_PATH } from 'app/lib/routing/constants';

const ROUTE_BY_TYPE: MatchByType<string> = {
  [NotificationType.DEPOSIT]: ROUTE_PATH.HISTORY,
  [NotificationType.WITHDRAWAL]: ROUTE_PATH.HISTORY,
  [NotificationType.CASHBACK]: ROUTE_PATH.HISTORY,
  [NotificationType.STAKING]: ROUTE_PATH.HISTORY_STAKES,
};

const STATUS_BY_TYPE: MatchByType<NotificationStatus> = {
  [NotificationType.DEPOSIT]: NotificationStatus.SUCCESS,
  [NotificationType.CASHBACK]: NotificationStatus.SUCCESS,
};

export function Notification<T extends BaseNotificationProps>({
  title,
  body,
  date,
  type = NotificationType.CUSTOM,
  className,
  balanceChange,
  currency,
  id,
  onClick,
}: NotificationProps<T>) {
  const { lang } = useTranslation();

  const statusContent = useMemo<ReactNode>(() => {
    if (!balanceChange) {
      return;
    };

    const formattedBalanceChange = formatMoney(balanceChange, lang);

    switch (type) {
      case NotificationType.CASHBACK:
      case NotificationType.DEPOSIT:
        return (
          <>
            +{formattedBalanceChange} <CurrencySymbol currency={currency} />
          </>
        );
      case NotificationType.WITHDRAWAL:
        return (
          <>
            −{formattedBalanceChange} <CurrencySymbol currency={currency} />
          </>
        );
    }
  }, [type, balanceChange, lang]);

  return (
    <BaseNotification to={ROUTE_BY_TYPE[type]}
                      className={className}
                      title={title}
                      body={body}
                      date={date}
                      status={STATUS_BY_TYPE[type]}
                      statusContent={statusContent}
                      onClick={onClick}
                      id={id} />
  );
};


